import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Layout,
  Clock,
  ArrowRight,
  Search,
  Filter,
  Database,
  MessageSquare,
  Trash2,
  Check,
  X,
  Pencil
} from 'lucide-react';
import { cn } from '../lib/utils';
import { showSuccessToast } from '../lib/toast';
import { workspaceApi } from '../api/workspaceApi';
import CreateWorkspaceWizard from '../components/workspace/CreateWorkspaceWizard';

const WorkspaceCard = ({ id, name, description, sources, lastAccessed, onClick, onDelete, onUpdate, chatsCount }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState(name || '');
  const [editedDescription, setEditedDescription] = useState(description || '');

  useEffect(() => {
    setEditedName(name || '');
    setEditedDescription(description || '');
  }, [name, description]);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (isEditing) return;
    setIsDeleting(true);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsDeleting(false);
    setIsEditing(true);
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditedName(name || '');
    setEditedDescription(description || '');
    setIsEditing(false);
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    const trimmedName = editedName.trim();
    if (!trimmedName) return;

    setIsSaving(true);
    try {
      await onUpdate(id, {
        name: trimmedName,
        description: editedDescription.trim()
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update workspace:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setIsDeleting(false);
  };

  const confirmDelete = (e) => {
    e.stopPropagation();
    onDelete(id);
    setIsDeleting(false);
  };

  return (
    <div
      onClick={() => !isDeleting && !isEditing && onClick(id)}
      className="group relative flex flex-col p-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl text-left hover:border-emerald-500/50 dark:hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 cursor-pointer"
    >
      <div className="flex items-center justify-between mb-6 w-full">
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl text-gray-400 group-hover:text-emerald-600 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/20 transition-all duration-300">
          <Layout size={24} />
        </div>

        {!isDeleting && !isEditing ? (
          <div className="flex items-center gap-1">
            <button
              onClick={handleEditClick}
              className="p-2 text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-all"
              title="Edit Workspace"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-all"
              title="Delete Workspace"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ) : (
          <div className={cn(
            "flex items-center gap-1 rounded-xl p-1 animate-in zoom-in-95 duration-200",
            isDeleting ? "bg-red-50 dark:bg-red-900/20" : "bg-emerald-50 dark:bg-emerald-900/20"
          )}>
            {isDeleting ? (
              <>
                <button
                  onClick={cancelDelete}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={confirmDelete}
                  className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow-sm"
                >
                  <Check size={14} />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={cancelEdit}
                  disabled={isSaving}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={saveEdit}
                  disabled={isSaving || !editedName.trim()}
                  className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all shadow-sm disabled:opacity-50"
                >
                  <Check size={14} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1">
        {isEditing ? (
          <div className="space-y-3 mb-4">
            <input
              className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-2xl px-4 py-3 text-gray-900 dark:text-white font-bold transition-all outline-none text-sm"
              placeholder="Workspace name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <textarea
              rows={2}
              className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-2xl px-4 py-3 text-gray-900 dark:text-white font-medium text-sm transition-all outline-none resize-none"
              placeholder="Description (optional)"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : (
          <>
            <h3 className={cn(
              "text-lg font-black uppercase tracking-tight mb-2 transition-colors",
              isDeleting ? "text-red-600" : "text-gray-900 dark:text-white group-hover:text-emerald-600"
            )}>
              {isDeleting ? "Delete space?" : (name || "Untitled Workspace")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
              {description || "No description provided."}
            </p>
          </>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-1.5">
            <Database size={14} className="text-gray-400" />
            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
              {sources?.length || 0} {sources?.length === 1 ? 'Source' : 'Sources'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 border-l border-gray-100 dark:border-gray-800 pl-4">
            <MessageSquare size={14} className="text-gray-400" />
            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
              {chatsCount || 0} {chatsCount === 1 ? 'Chat' : 'Chats'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-50 dark:border-gray-800 w-full">
        <div className="flex items-center gap-1.5 text-gray-400">
          <Clock size={12} />
          <span className="text-[10px] font-bold uppercase tracking-widest">{lastAccessed || 'Recently'}</span>
        </div>
        <ArrowRight size={18} className={cn("transform transition-all", (isDeleting || isEditing) ? "opacity-0" : "text-gray-300 group-hover:text-emerald-600 group-hover:translate-x-1")} />
      </div>
    </div>
  );
};

const WorkspacesPage = () => {
  useEffect(() => {
    document.title = 'manteo.ai | Workspaces';
  }, []);

  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadWorkspaces = async () => {
    setIsLoading(true);
    try {
      const data = await workspaceApi.getWorkspaces();
      setWorkspaces(data);
    } catch (err) {
      console.error('Failed to load workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteWorkspace = async (id) => {
    try {
      await workspaceApi.deleteWorkspace(id);
      setWorkspaces(prev => prev.filter(ws => ws.id !== id));
      showSuccessToast('Workspace removed.');
    } catch (err) {
      console.error('Failed to delete workspace:', err);
    }
  };

  const handleUpdateWorkspace = async (id, updates) => {
    try {
      const updatedWs = await workspaceApi.updateWorkspace(id, updates);
      setWorkspaces(prev => prev.map(ws => (ws.id === id ? updatedWs : ws)));
      showSuccessToast('Workspace updated.');
    } catch (err) {
      console.error('Failed to update workspace:', err);
      throw err;
    }
  };

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const filteredWorkspaces = workspaces.filter(ws =>
    ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ws.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-gray-950 px-8 py-12 transition-colors duration-300">
      <CreateWorkspaceWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={async (workspaceData) => {
          try {
            const newWs = await workspaceApi.createWorkspace(workspaceData);
            showSuccessToast('Workspace created.');
            navigate(`/workspace/${newWs.id}`);
          } catch (err) {
            console.error('Failed to create workspace:', err);
            throw err;
          }
        }}
      />

      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Workspaces</h1>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Manage your analysis environments</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-600/10 active:scale-95"
          >
            <Plus size={16} strokeWidth={3} />
            New Workspace
          </button>
        </header>

        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
            <h2 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Directory / All Spaces</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search workspaces..."
                  className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-200 dark:border-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors text-sm font-medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                <Filter size={20} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {isLoading ? (
              [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="h-64 rounded-3xl bg-gray-100 dark:bg-gray-900 animate-pulse" />
              ))
            ) : filteredWorkspaces.length > 0 ? (
              filteredWorkspaces.map(w => (
                <WorkspaceCard
                  key={w.id}
                  {...w}
                  onClick={(id) => navigate(`/workspace/${id}`)}
                  onDelete={handleDeleteWorkspace}
                  onUpdate={handleUpdateWorkspace}
                />
              ))
            ) : (
              <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-[40px]">
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No workspaces found</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default WorkspacesPage;
